import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function usePreventPinchZoom() {
  const { pathname } = useLocation();
  const enabled = !/^\/admin(?:\/|_main(?:\/|$)|$)/i.test(pathname);

  useLayoutEffect(() => {
    if (!enabled) return;

    const root = document.documentElement;
    const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const originalViewport = viewport?.getAttribute('content');
    const previousAttribute = root.getAttribute('data-prevent-pinch-zoom');
    root.setAttribute('data-prevent-pinch-zoom', 'true');
    if (viewport) {
      const settings = (originalViewport || 'width=device-width, initial-scale=1')
        .split(',').filter(setting => !/^\s*(maximum-scale|minimum-scale|user-scalable)\s*=/i.test(setting));
      viewport.content = [...settings, 'minimum-scale=1', 'maximum-scale=1', 'user-scalable=no'].join(', ');
    }

    const prevent = (event: Event) => { if (event.cancelable) event.preventDefault(); };
    const preventMultiTouch = (event: TouchEvent) => {
      if (event.touches.length > 1) prevent(event);
    };
    const preventTrackpadPinch = (event: WheelEvent) => {
      if (event.ctrlKey) prevent(event);
    };
    // Safari exposes pinch gestures separately and can ignore viewport scaling limits.
    const gestures = ['gesturestart', 'gesturechange', 'gestureend'];
    const options = { passive: false, capture: true };
    gestures.forEach(type => document.addEventListener(type, prevent, options));
    document.addEventListener('touchstart', preventMultiTouch, options);
    document.addEventListener('touchmove', preventMultiTouch, options);
    document.addEventListener('wheel', preventTrackpadPinch, options);

    return () => {
      gestures.forEach(type => document.removeEventListener(type, prevent, true));
      document.removeEventListener('touchstart', preventMultiTouch, true);
      document.removeEventListener('touchmove', preventMultiTouch, true);
      document.removeEventListener('wheel', preventTrackpadPinch, true);
      if (previousAttribute === null) root.removeAttribute('data-prevent-pinch-zoom');
      else root.setAttribute('data-prevent-pinch-zoom', previousAttribute);
      if (viewport) {
        if (originalViewport == null) viewport.removeAttribute('content');
        else viewport.setAttribute('content', originalViewport);
      }
    };
  }, [enabled]);
}
